import useSize from '@react-hook/size';
import { Alert } from 'antd';
import DataDownloader from 'components/DataDownloader';
import React, { useRef } from 'react';
import styled from 'styled-components';
import type { IWidgetBlockComponent } from '../types/IWidgetBlock';
import Axes from './components/Axes';
import Header from './components/Header';
import SpectraHeatmap from './components/SpectraHeatmap';
import type { SpectraModel } from './types';

const Wrapper = styled.div`
    display: flex;
    height: 100%;
    width: 100%;
    flex-direction: column;
`;

const Spectra = ({ widgetModel }: IWidgetBlockComponent<SpectraModel>) => {
    const {
        maxValue,
        xyValues,
        xAxisData,
        yAxisData,
        yAxisUnit,
        timezone,
        specdenUnits,
        viewblockState: { settings, databilities },
        minValue,
        datasetsWithData,
    } = widgetModel!;

    const containerDiv = useRef<HTMLDivElement>(null);
    const [boundsWidth, boundsHeight] = useSize(containerDiv);
    const containerDivBounds = {
        width: boundsWidth,
        height: boundsHeight,
    };

    const fullWidth = containerDivBounds.width;
    const fullHeight = containerDivBounds.height;
    const { yAxisDecimalPlaces, fontSize: fontSizePercent, displayLegend, displayGrid, displayTimeLegend } = settings;
    const fontSizeRatio = fontSizePercent / 100;
    const defaultLeftOffset = 50 * fontSizeRatio;
    const leftOffset = settings.useFixedOffset ? (settings.offsetLeft ?? defaultLeftOffset) : defaultLeftOffset;
    const tickSize = 18 * fontSizeRatio;
    const fontSize = 14 * fontSizeRatio;
    const tickSizeInner = 6;
    const topOffset = displayLegend ? Math.max(50 * fontSizeRatio, 35) : 0;
    const bottomOffset = tickSize * (displayTimeLegend ? 2 : 1) + tickSizeInner / 2;

    const canvasHeight = fullHeight - bottomOffset - topOffset;
    const canvasWidth = fullWidth - leftOffset;

    const databilityToUnit = databilities.reduce<{ [key: string]: string | null }>((prev, current) => {
        return { ...prev, [current.id]: 'specden' };
    }, {});

    return (
        <Wrapper>
            <DataDownloader
                datasetsWithData={datasetsWithData}
                databilities={databilities}
                databilityToUnit={databilityToUnit}
                timezone={timezone}
                zipName='spectra-timeseries'
            />
            <Header fontSize={settings.fontSize}>{settings.title}</Header>
            <div style={{ flex: 1, position: 'relative' }} ref={containerDiv}>
                <SpectraHeatmap
                    {...{
                        minValue,
                        maxValue,
                        canvasHeight,
                        canvasWidth,
                        xyValues,
                        blur: settings.blur,
                        leftOffset,
                        topOffset,
                    }}
                />
                <Axes
                    {...{
                        canvasHeight,
                        canvasWidth,
                        fontSizeRatio,
                        fontSize,
                        fullHeight,
                        fullWidth,
                        leftOffset,
                        tickSize,
                        tickSizeInner,
                        timezone,
                        xAxisData,
                        yAxisData,
                        yAxisDecimalPlaces,
                        yAxisUnit,
                        minValue,
                        maxValue,
                        topOffset,
                        specdenUnits,
                        displayGrid,
                        displayTimeLegend,
                    }}
                />
            </div>
        </Wrapper>
    );
};

const ConditionalSpectra = (props: IWidgetBlockComponent<SpectraModel>) => {
    if (props.error || !props.widgetModel) {
        return <Alert type='warning' message={`Spectra is not yet configured`} showIcon />;
    }
    return <Spectra {...props} />;
};

export default ConditionalSpectra;
